import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, type OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { AzureAuthService } from '@dtis/angular';
import { TranslocoModule, translate } from '@jsverse/transloco';
import { catchError, filter, map, type Observable, of, switchMap } from 'rxjs';
import { FeedbackApiService } from '../../services/api/feedback.service';
import { InternetConnectionStatusService } from '../../services/internet-connection-status.service';
import { SupportService } from '../../services/support-service';
import { SupportRequestDialogComponent } from '../support-request-dialog/support-request-dialog.component';

@Component({
  selector: 'dtis-header',
  templateUrl: './dtis-header.component.html',
  providers: [InternetConnectionStatusService],
  styles: [
    `
      .header-logo {
        background: url('/assets/dtis-schaeffler-header-logo.svg') no-repeat;
        height: 20px;
        width: 175px;
      }
      a:-webkit-any-link {
        color: #ffffffdd;
      }

      .text-fade-enter {
        opacity: 0;
        transform: translateX(-10%);
      }

      .text-fade-transition {
        opacity: 1;
        transform: translateX(-10%);
        animation: textFadeTransition 500ms ease-out forwards;
      }

      .text-fade-leave {
        opacity: 1;
        animation: textFadeLeave 500ms ease-out forwards;
      }

      @keyframes textFadeTransition {
        from {
          opacity: 1;
          transform: translateX(-10%);
        }
        to {
          opacity: 1;
          transform: translateX(0%);
        }
      }

      @keyframes textFadeLeave {
        from {
          opacity: 1;
        }
        to {
          opacity: 1;
        }
      }
    `,
  ],
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule, MatTooltipModule, MatSnackBarModule, TranslocoModule],
})
export class DtisHeaderComponent implements OnInit {
  snackbar = inject(MatSnackBar);
  dialog = inject(MatDialog);
  router = inject(Router);
  activated = inject(ActivatedRoute);
  titleService = inject(Title);
  auth = inject(AzureAuthService);
  connectionStatus = inject(InternetConnectionStatusService);
  supportService = inject(SupportService);
  feedbackAPI = inject(FeedbackApiService);
  /**
   * Can take a custome title
   */
  title = input.required<string>();
  /**
   * An Link to point the helper icons to
   * If not present the icon will not be rendered
   */
  helplink = input<string>();
  /**
   * An Link to point the helper icons to
   * If not present the icon will not be rendered
   */
  helplabel = input('Contact via Email');
  /**
   * indicates if the current route is the default route. To obtain if the navigation arrow should be displayed
   */
  isDefaultRoute = true;
  /**
   * used for route title
   */
  secondaryTitle = signal('');
  /**
   * Serves the azure profile picture
   */
  profilePicture$!: Observable<string | null>;
  /**
   *
   */
  fullTitle = computed(() => `${this.title()} - ${this.secondaryTitle()}`);
  /**
   * Contain the name of the current user
   */
  username$!: Observable<string>;
  /**
   * Contain the initials fo the current user
   */
  initalizes$!: Observable<unknown>;
  /**
   * The current environment loaded
   */
  environment!: string;
  /**
   *
   */
  isOffline = this.connectionStatus.isOffline;
  /**
   * modifies the username to remap to initials
   */
  public ngOnInit() {
    // Case of overview page events
    this.#setTitleAndHeadline();
    this.profilePicture$ = this.auth.getProfileImage().pipe(catchError(() => of(null)));
    const mail = this.auth.handleAccount()?.username;

    this.initalizes$ = of(mail).pipe(
      map((username) => {
        if (!username) {
          return '';
        }

        return username
          .split(', ')
          .map((username_part: string) => username_part.charAt(0))
          .reverse()
          .join()
          .toUpperCase()
          .replace(',', ''); // convert basically "Geissler, Benjamin SSAFSDSDSA" to "BG"}
      })
    );
  }
  /**
   * captures devices and navigationends events to display nicer titles with some informations
   */
  #setTitleAndHeadline() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        switchMap(() => this.activated.children),
        switchMap((r) => r.data)
      )
      .subscribe((event) => {
        // biome-ignore lint/complexity/useLiteralKeys: event is of type Data, which is just a map with string keys, so we have to use string keys to access the data
        this.secondaryTitle.set(event['label']);
        // biome-ignore lint/complexity/useLiteralKeys: event is of type Data, which is just a map with string keys, so we have to use string keys to access the data
        this.isDefaultRoute = event['defaultRoute'];
        this.titleService.setTitle(this.fullTitle());
      });
  }
  /**
   * Opens the support request dialog
   */
  async openSupportRequestDialog() {
    const dialogData = { screenshot: null as string | null };
    const dialogRef = this.dialog.open(SupportRequestDialogComponent, {
      data: dialogData,
    });

    const { createContext, domToPng } = await import('modern-screenshot');
    const context = await createContext(document.body, {});
    domToPng(context)
      .then((screenshot) => {
        dialogData.screenshot = screenshot;
        // biome-ignore lint/suspicious/noExplicitAny: no explanation
        (dialogRef.componentInstance as any).dialogData = dialogData;
      })
      .catch((err) => {
        this.snackbar.open(translate('could-not-capture-screenshot'), translate('close'), {
          duration: 5000,
        });
        console.error(err);
      });
  }
}
